import { Injectable, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { INGXLoggerConfig, INGXLoggerMetadata, NGXLoggerWriterService } from 'ngx-logger';
import { LogStorageService } from './log-storage.service';
import { redactSensitiveData, shouldRetainLog } from './log-redaction';
import { LOG_BATCH_PREFIX, readRecentLogBatches, selectLogBatchKeys } from './log-batches';

@Injectable()
export class LoggerWriterService extends NGXLoggerWriterService implements OnDestroy {
  private readonly BATCH_SIZE = 50; // Maximum logs per batch
  private readonly FLUSH_INTERVAL = 10000; // 10 seconds in ms
  private readonly MAX_BATCHES = 20; // Maximum number of batches to keep
  private readonly LEGACY_STORAGE_PREFIX = 'logs_batch_';
  
  private logBuffer: INGXLoggerMetadata[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private batchCounter = 0;
  private readonly initialized: Promise<void>;
  private pendingFlush: Promise<void> = Promise.resolve();
  private readonly unloadListener = () => { void this.flushLogs(); };
  
  constructor(@Inject(PLATFORM_ID) platformId: any, private readonly storage: LogStorageService) {
    super(platformId);
    // Initialize by getting the current batch counter from IndexedDB
    this.initialized = this.initBatchCounter();
    // Set up flush timer
    this.setupFlushTimer();
    // Set up window unload listener to flush logs before page close
    this.setupUnloadListener();
  }

  public override writeMessage(metadata: INGXLoggerMetadata, config: INGXLoggerConfig): void {
    if (!shouldRetainLog(metadata.level, config.level)) {
      return;
    }

    const redactedMetadata: INGXLoggerMetadata = {
      ...metadata,
      message: redactSensitiveData(metadata.message),
      additional: metadata.additional?.map((item: unknown) => redactSensitiveData(item)),
    };

    // Call the original writeMessage method to log to the console
    super.writeMessage(redactedMetadata, config);
    
    // Add log to buffer
    this.logBuffer.push(redactedMetadata);
    
    // Flush if buffer reaches batch size
    if (this.logBuffer.length >= this.BATCH_SIZE) {
      this.flushLogs();
    }
  }
  
  private async initBatchCounter(): Promise<void> {
    try {
      const existingKeys = await this.storage.keys();
      const legacyKeys = existingKeys.filter((key: unknown) =>
        typeof key === 'string' && key.startsWith(this.LEGACY_STORAGE_PREFIX)
      );
      await Promise.all(legacyKeys.map((key) => this.storage.delete(key as string)));
      const logKeys = selectLogBatchKeys(existingKeys);
      
      if (logKeys.length > 0) {
        // Extract the highest batch number
        const batchNumbers = logKeys.map(key => {
          const match = key.match(/\d+$/);
          return match ? parseInt(match[0], 10) : 0;
        });
        
        this.batchCounter = Math.max(...batchNumbers) + 1;
      }
    } catch (error) {
      console.error('Error initializing batch counter:', error);
    }
  }
  
  private setupFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      if (this.logBuffer.length > 0) {
        this.flushLogs();
      }
    }, this.FLUSH_INTERVAL);
  }
  
  private setupUnloadListener(): void {
    window.addEventListener('beforeunload', this.unloadListener);
  }
  
  private flushLogs(): Promise<void> {
    if (this.logBuffer.length === 0) return this.pendingFlush;
    
    const logsToFlush = [...this.logBuffer];
    this.logBuffer = []; // Clear buffer
    
    this.pendingFlush = this.pendingFlush.then(async () => {
      await this.initialized;
      const batchKey = `${LOG_BATCH_PREFIX}${this.batchCounter++}`;
      try {
        await this.storage.set(batchKey, logsToFlush);
        await this.pruneOldBatches();
      } catch (error) {
        console.error('Error flushing logs to IndexedDB:', error);
        // In case of error, try to recover some logs for critical errors
        if (logsToFlush.some(log => log.level >= 5)) { // Error or Fatal logs
          console.warn('Attempting to save critical logs to console');
          console.error('Critical logs that failed to be stored:', 
            logsToFlush.filter(log => log.level >= 5));
        }
      }
    });
    return this.pendingFlush;
  }
  
  private async pruneOldBatches(): Promise<void> {
    try {
      const logKeys = selectLogBatchKeys(await this.storage.keys());
      
      // If we have more batches than MAX_BATCHES, delete the oldest ones
      if (logKeys.length > this.MAX_BATCHES) {
        const keysToDelete = logKeys.slice(0, logKeys.length - this.MAX_BATCHES);
        for (const key of keysToDelete) {
          await this.storage.delete(key);
        }
      }
    } catch (error) {
      console.error('Error pruning old log batches:', error);
    }
  }
  
  // Method to retrieve logs for reporting
  public async getRecentLogs(limit: number = 100): Promise<INGXLoggerMetadata[]> {
    if (limit <= 0) return [];
    try {
      // Flush current logs to storage first
      await this.initialized;
      await this.flushLogs();
      
      return await readRecentLogBatches<INGXLoggerMetadata>(this.storage, limit);
    } catch (error) {
      console.error('Error retrieving recent logs:', error);
      return [];
    }
  }
  
  ngOnDestroy(): void {
    // Clear flush timer
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    window.removeEventListener('beforeunload', this.unloadListener);
    
    // Attempt to flush any remaining logs
    if (this.logBuffer.length > 0) {
      void this.flushLogs();
    }
  }
}
